import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useT } from "@/i18n/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import PromoBanner from "@/components/PromoBanner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_prenota-diretto/artifacts/qjerng52_logo%20mm.png";

export default function Navbar() {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const overHero = !scrolled && location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", key: "nav.home", anchor: null },
    { to: "/#appartamento", key: "nav.apartment", anchor: "appartamento" },
    { to: "/#amenities", key: "nav.amenities", anchor: "amenities" },
    { to: "/#dove-siamo", key: "nav.location", anchor: "dove-siamo" },
    { to: "/#faq", key: "nav.faq", anchor: "faq" },
    { to: "/#contatti", key: "nav.contact", anchor: "contatti" },
  ];

  const handleNavClick = (e, anchor) => {
    if (!anchor) return;
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <PromoBanner />
      <header
      data-testid="site-navbar"
      className={`fixed top-8 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-cream-200" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className={`w-11 h-11 rounded-2xl grid place-items-center transition-all ${overHero ? "bg-white/95 shadow-soft" : "bg-white shadow-soft"}`}>
            <img src={LOGO_URL} alt="Attico Skyline Milano" className="w-7 h-7 object-contain" />
          </span>
          <span className={`text-base font-bold tracking-tightest transition-colors ${overHero ? "text-white" : "text-slate-900"} group-hover:text-navy-600`}>
            Attico Skyline Milano
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <li key={l.key}>
              <Link
                to={l.to}
                onClick={(e) => handleNavClick(e, l.anchor)}
                data-testid={`nav-link-${l.key.replace(/[^a-z]/g, "-")}`}
                className={`text-sm font-medium px-4 py-2 rounded-full transition-colors ${
                  overHero
                    ? "text-white/95 hover:bg-white/15"
                    : "text-slate-700 hover:bg-cream-100 hover:text-slate-900"
                }`}
              >
                {t(l.key)}
              </Link>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <LanguageSelector inverted={overHero} />
          <Link
            to="/prenotazione"
            data-testid="nav-cta-prenota"
            className="hidden sm:inline-flex items-center justify-center rounded-full bg-navy-700 hover:bg-navy-800 text-white text-sm font-semibold px-6 py-2.5 transition-all hover:-translate-y-0.5 shadow-soft hover:shadow-elevate"
          >
            {t("nav.book")}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-toggle"
                className={`lg:hidden p-2.5 rounded-full transition-colors ${overHero ? "text-white bg-white/10 hover:bg-white/20" : "text-slate-900 bg-cream-100 hover:bg-cream-200"}`}
                aria-label={t("nav.menu")}
              >
                <Menu size={20} strokeWidth={2.2} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-[88%] sm:w-[400px] border-l border-cream-200">
              <div className="flex flex-col h-full pt-6">
                <div className="flex items-center justify-between mb-10">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-cream-100 grid place-items-center">
                      <img src={LOGO_URL} alt="" className="w-6 h-6 object-contain" />
                    </span>
                    <span className="text-base font-bold text-slate-900">Attico Skyline Milano</span>
                  </div>
                  <button onClick={() => setOpen(false)} className="text-slate-700 p-2" aria-label={t("nav.close")}>
                    <X size={20} />
                  </button>
                </div>
                <ul className="flex flex-col gap-1">
                  {links.map((l) => (
                    <li key={l.key}>
                      <Link
                        to={l.to}
                        onClick={(e) => { handleNavClick(e, l.anchor); setOpen(false); }}
                        data-testid={`mobile-nav-link-${l.key.replace(/[^a-z]/g, "-")}`}
                        className="block text-2xl font-semibold py-2.5 text-slate-900 hover:text-navy-600 transition-colors tracking-tight"
                      >
                        {t(l.key)}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/prenotazione"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta-prenota"
                  className="mt-auto mb-6 inline-flex items-center justify-center rounded-full bg-navy-700 hover:bg-navy-800 text-white text-base font-semibold px-8 py-4 transition-all"
                >
                  {t("nav.book_now")}
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
    </>
  );
}
